import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { PATHS } from '../../router/paths';
import {
  Users,
  Building2,
  Store,
  TrendingUp,
  Calendar,
  DollarSign,
  Activity,
  AlertTriangle,
  Clock,
  CheckCircle2,
  RefreshCw
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import StatWidget from '@components/back-office/shared/stat-widget';
import { useGetDashboard } from '@utils/api/back-office/dashboard/use-get-dashboard';
import { useGetAdminExceedances } from '@utils/api/back-office/exceedances/use-get-admin-exceedances';
import BugBoardWidget from '@components/back-office/bug-board/bug-board-widget';
import { IS_ZAPFEED_ENABLED } from '@utils/constants/app-constants';

export default function DashboardBo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();

  // Use the aggregated dashboard hook
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } =
    useGetDashboard();

  const { data: exceedanceData, isLoading: exceedancesLoading } =
    useGetAdminExceedances();

  const exceedanceCount = exceedanceData?.result?.length ?? 0;

  const customerMetrics = data?.customers ?? {
    totalActive: 0,
    totalOrganizations: 0,
    totalResellers: 0
  };

  const queueMetrics = data?.queue ?? {
    queueDepth: 0,
    errorCount: 0,
    strandedCount: 0,
    processingCount: 0
  };

  // Format last updated time
  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <div className="bg-app flex-1 px-4 py-2">
      <div className="flex items-start justify-between mb-2">
        <PageHeader
          title={t('dashboard')}
          caption={t('high-level-overview-of-system-health-and-metrics')}
        />

        {/* Auto-refresh indicator */}
        <div className="flex items-center gap-2 text-xs mt-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-hairline bg-app-soft hover:opacity-80 transition-opacity disabled:opacity-60"
            style={{ color: getColour('inactiveText') }}
            title={t('refresh')}
          >
            <RefreshCw
              className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
            />
            {lastUpdated && (
              <span style={{ color: getColour('inactiveText') }}>
                {lastUpdated}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Partial data warning */}
      {data?.isPartialData && (
        <div className="mb-4 p-3 rounded-2xl border text-sm bg-amber-400/10 border-amber-400/30 dark:bg-amber-400/15 dark:border-amber-400/40 text-amber-700 dark:text-amber-400">
          <AlertTriangle className="inline h-4 w-4 mr-2" />
          {t('some-data-may-be-unavailable')}
        </div>
      )}

      {/* Customer & Organization Metrics */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('customers-and-organizations')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatWidget
            title={t('active-customers')}
            value={customerMetrics.totalActive}
            icon={<Users className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
            onClick={() => navigate(PATHS().CUSTOMERS + '?q=is_active:eq:true')}
          />
          <StatWidget
            title={t('total-organizations')}
            value={customerMetrics.totalOrganizations}
            icon={<Building2 className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
            onClick={() => navigate(PATHS().CUSTOMERS)}
          />
          <StatWidget
            title={t('active-resellers')}
            value={customerMetrics.totalResellers}
            icon={<Store className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
            onClick={() =>
              navigate(PATHS().CUSTOMERS + '?q=is_reseller:eq:true')
            }
          />
          <StatWidget
            title={t('plan-exceedances')}
            value={exceedanceCount}
            subtitle={
              exceedanceCount > 0
                ? `${exceedanceCount} ${t('organizations-over-limit')}`
                : t('all-within-limits')
            }
            icon={<AlertTriangle className="h-5 w-5" />}
            variant={exceedanceCount > 0 ? 'danger' : 'success'}
            loading={exceedancesLoading}
            onClick={() => navigate(PATHS().BO_EXCEEDANCES)}
          />
        </div>
      </section>

      {/* Usage & Revenue Metrics */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('usage-and-revenue')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatWidget
            title={t('current-month-usage')}
            value={t('view-details')}
            subtitle={t('click-to-see-breakdown')}
            icon={<TrendingUp className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
            onClick={() => navigate(PATHS().BO_USAGE_REVENUE + '?tab=0')}
          />
          <StatWidget
            title={t('projected-end-of-month')}
            value={t('view-forecast')}
            subtitle={t('based-on-current-usage')}
            icon={<Calendar className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
            onClick={() => navigate(PATHS().BO_USAGE_REVENUE + '?tab=0')}
          />
          <StatWidget
            title={t('last-month-billed')}
            value={t('view-billables')}
            subtitle={t('previous-billing-period')}
            icon={<DollarSign className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
            onClick={() => navigate(PATHS().BO_USAGE_REVENUE + '?tab=2')}
          />
        </div>
      </section>

      {/* Operational Health */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('operational-health')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatWidget
            title={t('queue-depth')}
            value={queueMetrics.queueDepth}
            subtitle={t('items-in-queue')}
            icon={<Clock className="h-5 w-5" />}
            variant={queueMetrics.queueDepth > 100 ? 'warning' : 'default'}
            loading={isLoading}
            onClick={() => navigate(PATHS().BO_OPERATIONS + '?status=queued')}
          />
          <StatWidget
            title={t('processing')}
            value={queueMetrics.processingCount}
            subtitle={t('currently-processing')}
            icon={<Activity className="h-5 w-5" />}
            variant="success"
            loading={isLoading}
            onClick={() =>
              navigate(PATHS().BO_OPERATIONS + '?status=processing')
            }
          />
          <StatWidget
            title={t('errors')}
            value={queueMetrics.errorCount}
            subtitle={t('failed-and-dead-letter')}
            icon={<AlertTriangle className="h-5 w-5" />}
            variant={queueMetrics.errorCount > 0 ? 'danger' : 'success'}
            loading={isLoading}
            onClick={() => navigate(PATHS().BO_OPERATIONS + '?status=error')}
          />
          <StatWidget
            title={t('stranded-sessions')}
            value={queueMetrics.strandedCount}
            subtitle={t('needs-attention')}
            icon={
              queueMetrics.strandedCount > 0 ? (
                <AlertTriangle className="h-5 w-5" />
              ) : (
                <CheckCircle2 className="h-5 w-5" />
              )
            }
            variant={queueMetrics.strandedCount > 0 ? 'danger' : 'success'}
            loading={isLoading}
            onClick={() => navigate(PATHS().BO_OPERATIONS + '?status=stranded')}
          />
        </div>
      </section>

      {/* Bug Board (Zapfeed) — hidden unless configured at build time */}
      {IS_ZAPFEED_ENABLED && (
        <section className="mb-8">
          <h2
            className="text-sm font-semibold mb-4 uppercase tracking-wide"
            style={{ color: getColour('inactiveText') }}
          >
            {t('bug-board')}
          </h2>
          <BugBoardWidget />
        </section>
      )}

      {/* Quick Actions */}
      <section>
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('quick-actions')}
        </h2>
        <div className="rounded-2xl border border-hairline bg-app-soft shadow-card p-4">
          <p
            className="text-sm mb-4"
            style={{ color: getColour('normalText') }}
          >
            {t(
              'use-the-navigation-to-access-detailed-views-and-perform-specific-actions'
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              { label: t('manage-customers'), path: PATHS().CUSTOMERS },
              { label: t('view-plans'), path: PATHS().PLANS },
              { label: t('operations-center'), path: PATHS().BO_OPERATIONS },
              { label: t('settings'), path: PATHS().BO_SETTINGS }
            ].map(action => (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className="px-4 py-2 rounded-full border border-hairline bg-app text-sm font-medium transition-opacity hover:opacity-80"
                style={{ color: getColour('normalText') }}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
